import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X, Minimize2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface TranslationWarningBadgeProps {
  /** Whether browser translation is currently detected */
  isTranslationActive: boolean;
  /** The language that was detected */
  detectedLanguage?: string;
  /** Callback to disable translation and reload */
  onDisableTranslation: () => void;
  /** Whether the disable action is processing */
  isProcessing?: boolean;
  /** Custom position class */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  /** Custom CSS classes */
  className?: string;
}

export function TranslationWarningBadge({
  isTranslationActive,
  detectedLanguage,
  onDisableTranslation,
  isProcessing = false,
  position = 'bottom-right',
  className = ''
}: TranslationWarningBadgeProps) {
  const { t } = useTranslation();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Don't render if translation is not active or user has hidden it
  if (!isTranslationActive || !isVisible) {
    return null;
  }

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
  };

  const handleDisable = () => {
    if (!isProcessing) {
      onDisableTranslation();
    }
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleHide = () => {
    setIsVisible(false);
  };

  // Minimized view - just a small floating badge
  if (isMinimized) {
    return (
      <div
        className={`fixed z-50 ${positionClasses[position]} ${className}`}
        role="alert"
        aria-live="polite"
      >
        <Button
          onClick={handleMinimize}
          variant="destructive"
          size="sm"
          className="h-10 px-3 rounded-full shadow-lg animate-pulse hover:animate-none"
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          <span className="text-xs font-medium">
            {t('translation.warning.restore')}
          </span>
        </Button>
      </div>
    );
  }

  // Full warning badge
  return (
    <div
      className={`fixed z-50 ${positionClasses[position]} ${className}`}
      role="alert"
      aria-live="polite"
    >
      <Card className="w-72 border-2 border-yellow-400 shadow-lg bg-yellow-50">
        <CardContent className="p-3">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-yellow-900 text-sm">
                  {t('translation.warning.title')}
                </h4>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-1">
              <Button
                onClick={handleMinimize}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-yellow-700 hover:bg-yellow-100"
                title="Minimize"
              >
                <Minimize2 className="h-3 w-3" />
              </Button>
              <Button
                onClick={handleHide}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-yellow-700 hover:bg-yellow-100"
                title="Hide"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Message */}
          <div className="mb-3">
            <p className="text-yellow-800 text-xs mb-1">
              {t('translation.warning.message')}
            </p>
            {detectedLanguage && (
              <Badge variant="outline" className="text-xs text-yellow-700 border-yellow-300">
                {detectedLanguage.toUpperCase()}
              </Badge>
            )}
          </div>

          {/* Warning about issues */}
          <div className="mb-3 p-2 bg-yellow-100 rounded text-xs text-yellow-800">
            <span className="font-medium">⚠️ {t('translation.warning.issues')}</span>
          </div>

          {/* Action Button */}
          <div className="flex gap-2">
            <Button
              onClick={handleDisable}
              disabled={isProcessing}
              size="sm"
              variant="outline"
              className="flex-1 text-xs h-8 border-yellow-400 text-yellow-800 hover:bg-yellow-100"
            >
              {isProcessing ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin h-3 w-3 border border-yellow-600 border-t-transparent rounded-full"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <RotateCcw className="h-3 w-3" />
                  <span>{t('translation.warning.disable')}</span>
                </div>
              )}
            </Button>
          </div>

          {/* Help text */}
          <div className="mt-2 text-xs text-yellow-700">
            Use Flexireo's language selector for better experience
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default TranslationWarningBadge;